import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'

interface VideoDialogProps {
    open: boolean
    videoId: string
    title: string
    onClose: () => void
}

const VideoDialog: FC<VideoDialogProps> = ({ open, videoId, title, onClose }): ReactNode => {
    const closeRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        if (!open) return

        const previousOverflow = document.body.style.overflow

        document.body.style.overflow = 'hidden'
        closeRef.current?.focus()

        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }

        window.addEventListener('keydown', handleKey)

        return () => {
            document.body.style.overflow = previousOverflow
            window.removeEventListener('keydown', handleKey)
        }
    }, [open, onClose])

    if (!open) return null

    return createPortal(
        <div
            role='dialog'
            aria-modal='true'
            aria-label={title}
            onClick={onClose}
            className='fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm animate-in fade-in duration-200 sm:p-10'
        >
            <span tabIndex={0} onFocus={() => closeRef.current?.focus()} />

            <div
                onClick={event => event.stopPropagation()}
                className='relative w-full max-w-5xl animate-in fade-in zoom-in-95 duration-300 ease-out'
            >
                <button
                    ref={closeRef}
                    type='button'
                    onClick={onClose}
                    aria-label='Close video'
                    className='absolute -top-12 right-0 grid size-9 place-items-center rounded-full border border-white/15 bg-white/10 text-white transition-colors hover:bg-white/20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white'
                >
                    <X className='size-4' />
                </button>

                <div className='aspect-video overflow-hidden rounded-xl border border-white/10 bg-black shadow-[0_30px_120px_rgb(0_0_0/0.8)]'>
                    <iframe
                        className='size-full'
                        src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
                        title={title}
                        allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                        referrerPolicy='strict-origin-when-cross-origin'
                        allowFullScreen
                    />
                </div>
            </div>

            <span tabIndex={0} onFocus={() => closeRef.current?.focus()} />
        </div>,
        document.body
    )
}

export default VideoDialog
